import { useEffect } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import Modal from '@/components/common/modal';
import { useCreateProductMutation } from '@/state/mutations/admin-products/use-create-product-mutation';
import { useUpdateProductMutation } from '@/state/mutations/admin-products/use-update-product-mutation';
import type { ProductDto } from '@/types/dtos/orders/response/product.dto';
import { PRODUCT_KINDS } from '@/types/dtos/orders/response/product.dto';
import { ServerError } from '@/types/errors/server.error';

const SCOPE_TYPES = ['Event', 'Organization', 'Flow', 'Venue'] as const;

const defaultPackageJson = JSON.stringify(
  {
    assignableTo: ['Flow'],
    constraints: {},
    includes: { quotas: [], features: [] },
  },
  null,
  2,
);

const formSchema = z
  .object({
    key: z.string().min(1, 'Key is required'),
    kind: z.enum([PRODUCT_KINDS.QUOTA, PRODUCT_KINDS.FEATURE, PRODUCT_KINDS.PACKAGE]),
    name: z.string().min(1),
    description: z.string().optional(),
    scopeType: z.enum(SCOPE_TYPES),
    unit: z.string().optional(),
    pricePerUnit: z.coerce.number().nonnegative(),
    isMultipleAllowed: z.boolean(),
    validFrom: z.string().optional(),
    validUntil: z.string().optional(),
    quotaKey: z.string().optional(),
    quantityPerUnit: z.coerce.number().positive().optional(),
    featureKey: z.string().optional(),
    packageConfigJson: z.string().optional(),
    cancelScheduledPrice: z.boolean(),
    scheduleNewPrice: z.boolean(),
    scheduledPricePerUnit: z.coerce.number().nonnegative().optional(),
    scheduledEffectiveFrom: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (data.kind === PRODUCT_KINDS.QUOTA) {
      if (!data.quotaKey?.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Quota key required', path: ['quotaKey'] });
      }
      if (data.quantityPerUnit === undefined) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Quantity per unit required', path: ['quantityPerUnit'] });
      }
    }
    if (data.kind === PRODUCT_KINDS.FEATURE && !data.featureKey?.trim()) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Feature key required', path: ['featureKey'] });
    }
    if (data.kind === PRODUCT_KINDS.PACKAGE) {
      try {
        const parsed = JSON.parse(data.packageConfigJson || '{}') as unknown;
        if (typeof parsed !== 'object' || parsed === null || !('includes' in parsed)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'packageConfig must be JSON with includes',
            path: ['packageConfigJson'],
          });
        }
      } catch {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Invalid JSON', path: ['packageConfigJson'] });
      }
    }
    if (data.scheduleNewPrice) {
      if (data.scheduledPricePerUnit === undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Future price required',
          path: ['scheduledPricePerUnit'],
        });
      }
      if (!data.scheduledEffectiveFrom?.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Effective date required',
          path: ['scheduledEffectiveFrom'],
        });
      } else {
        const eff = new Date(data.scheduledEffectiveFrom);
        if (Number.isNaN(eff.getTime()) || eff.getTime() <= Date.now()) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Must be a future date/time',
            path: ['scheduledEffectiveFrom'],
          });
        }
      }
    }
  });

type FormData = z.infer<typeof formSchema>;

function toIsoOptional(v: string | undefined): string | undefined {
  if (!v?.trim()) return undefined;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? undefined : d.toISOString();
}

function buildCreateBody(data: FormData): Record<string, unknown> {
  const body: Record<string, unknown> = {
    key: data.key.trim(),
    kind: data.kind,
    name: data.name.trim(),
    description: data.description?.trim() || undefined,
    scopeType: data.scopeType,
    unit: data.unit?.trim() || undefined,
    pricePerUnit: data.pricePerUnit,
    isMultipleAllowed: data.isMultipleAllowed,
    validFrom: toIsoOptional(data.validFrom),
    validUntil: toIsoOptional(data.validUntil),
  };
  if (data.kind === PRODUCT_KINDS.QUOTA) {
    body.quotaConfig = {
      quotaKey: data.quotaKey!.trim(),
      quantityPerUnit: data.quantityPerUnit ?? 1,
    };
  } else if (data.kind === PRODUCT_KINDS.FEATURE) {
    body.featureConfig = { featureKey: data.featureKey!.trim() };
  } else {
    body.packageConfig = JSON.parse(data.packageConfigJson || '{}') as Record<string, unknown>;
  }
  if (data.scheduleNewPrice && data.scheduledEffectiveFrom?.trim() && data.scheduledPricePerUnit !== undefined) {
    const effectiveFrom = toIsoOptional(data.scheduledEffectiveFrom);
    if (effectiveFrom) {
      body.scheduledPrice = {
        pricePerUnit: data.scheduledPricePerUnit,
        effectiveFrom,
      };
    }
  }
  return body;
}

function buildUpdateBody(data: FormData, existing: ProductDto): Record<string, unknown> {
  const body: Record<string, unknown> = {
    name: data.name.trim(),
    description: data.description?.trim() || undefined,
    scopeType: data.scopeType,
    unit: data.unit?.trim() || undefined,
    pricePerUnit: data.pricePerUnit,
    isMultipleAllowed: data.isMultipleAllowed,
    validFrom: toIsoOptional(data.validFrom),
    validUntil: toIsoOptional(data.validUntil),
  };
  if (data.kind === PRODUCT_KINDS.QUOTA) {
    body.quotaConfig = {
      quotaKey: data.quotaKey!.trim(),
      quantityPerUnit: data.quantityPerUnit ?? 1,
    };
  } else if (data.kind === PRODUCT_KINDS.FEATURE) {
    body.featureConfig = { featureKey: data.featureKey!.trim() };
  } else {
    body.packageConfig = JSON.parse(data.packageConfigJson || '{}') as Record<string, unknown>;
  }

  if (data.cancelScheduledPrice) {
    body.cancelScheduledPrice = true;
  }

  if (data.scheduleNewPrice && data.scheduledEffectiveFrom?.trim() && data.scheduledPricePerUnit !== undefined) {
    const effectiveFrom = toIsoOptional(data.scheduledEffectiveFrom);
    if (effectiveFrom) {
      body.scheduledPrice = {
        pricePerUnit: data.scheduledPricePerUnit,
        effectiveFrom,
      };
    }
  }

  const priceChanged = data.pricePerUnit !== existing.pricePerUnit;
  if (existing.scheduledPrice && priceChanged && !body.scheduledPrice && !data.cancelScheduledPrice) {
    body.cancelScheduledPrice = true;
  }

  return body;
}

function defaultsFromProduct(p: ProductDto): FormData {
  return {
    key: p.key,
    kind: p.kind,
    name: p.name,
    description: p.description ?? '',
    scopeType: (SCOPE_TYPES as readonly string[]).includes(p.scopeType)
      ? (p.scopeType as FormData['scopeType'])
      : 'Event',
    unit: p.unit ?? '',
    pricePerUnit: p.pricePerUnit,
    isMultipleAllowed: p.isMultipleAllowed,
    validFrom: p.validFrom ? p.validFrom.slice(0, 16) : '',
    validUntil: p.validUntil ? p.validUntil.slice(0, 16) : '',
    quotaKey: p.quotaConfig?.quotaKey ?? '',
    quantityPerUnit: p.quotaConfig?.quantityPerUnit ?? 1,
    featureKey: p.featureConfig?.featureKey ?? '',
    packageConfigJson: p.packageConfig ? JSON.stringify(p.packageConfig, null, 2) : defaultPackageJson,
    cancelScheduledPrice: false,
    scheduleNewPrice: false,
    scheduledPricePerUnit: p.scheduledPrice?.pricePerUnit ?? undefined,
    scheduledEffectiveFrom: p.scheduledPrice?.effectiveFrom ? p.scheduledPrice.effectiveFrom.slice(0, 16) : '',
  };
}

const emptyCreate: FormData = {
  key: '',
  kind: PRODUCT_KINDS.QUOTA,
  name: '',
  description: '',
  scopeType: 'Event',
  unit: '',
  pricePerUnit: 0,
  isMultipleAllowed: true,
  validFrom: '',
  validUntil: '',
  quotaKey: '',
  quantityPerUnit: 1,
  featureKey: '',
  packageConfigJson: defaultPackageJson,
  cancelScheduledPrice: false,
  scheduleNewPrice: false,
  scheduledPricePerUnit: undefined,
  scheduledEffectiveFrom: '',
};

interface ProductAdminFormModalProps {
  open: boolean;
  onClose: () => void;
  product: ProductDto | null;
}

export default function ProductAdminFormModal({ open, onClose, product }: ProductAdminFormModalProps) {
  const isEdit = !!product;
  const createMut = useCreateProductMutation();
  const updateMut = useUpdateProductMutation();

  const {
    register,
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    mode: 'onBlur',
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(formSchema) as any,
    defaultValues: emptyCreate,
  });

  const kind = useWatch({ control, name: 'kind' });
  const scheduleNewPrice = useWatch({ control, name: 'scheduleNewPrice' });

  useEffect(() => {
    if (!open) return;
    reset(product ? defaultsFromProduct(product) : emptyCreate);
  }, [open, product, reset]);

  const onSubmit = async (data: FormData) => {
    try {
      if (isEdit && product) {
        if (data.pricePerUnit !== product.pricePerUnit) {
          const ok = window.confirm(
            'Change the live price? The current price will be closed in price history (server-side). Continue?',
          );
          if (!ok) {
            return;
          }
        }
        await updateMut.mutateAsync({ id: product.id, body: buildUpdateBody(data, product) });
      } else {
        await createMut.mutateAsync(buildCreateBody(data));
      }
      onClose();
    } catch (e) {
      if (e instanceof ServerError) {
        window.alert(e.message);
      } else {
        window.alert(e instanceof Error ? e.message : 'Request failed');
      }
    }
  };

  const busy = createMut.isPending || updateMut.isPending;

  return (
    <Modal
      open={open}
      handleClose={onClose}
      title={isEdit ? 'Edit product' : 'Create product'}
      size="lg"
      maxHeight="90vh"
      footer={
        <Stack direction="row" justifyContent="flex-end" gap={1} sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSubmit(onSubmit)} disabled={busy}>
            {isEdit ? 'Save' : 'Create'}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={2} sx={{ p: 2, overflow: 'auto' }}>
        {!isEdit && (
          <TextField
            label="Key (unique)"
            {...register('key')}
            error={!!errors.key}
            helperText={errors.key?.message}
            required
          />
        )}
        <Controller
          name="kind"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth disabled={isEdit}>
              <InputLabel id="kind-label">Kind</InputLabel>
              <Select labelId="kind-label" label="Kind" {...field}>
                <MenuItem value={PRODUCT_KINDS.QUOTA}>QUOTA</MenuItem>
                <MenuItem value={PRODUCT_KINDS.FEATURE}>FEATURE</MenuItem>
                <MenuItem value={PRODUCT_KINDS.PACKAGE}>PACKAGE</MenuItem>
              </Select>
            </FormControl>
          )}
        />
        <TextField
          label="Name"
          {...register('name')}
          error={!!errors.name}
          helperText={errors.name?.message}
          required
        />
        <TextField label="Description" {...register('description')} multiline minRows={2} />
        <Controller
          name="scopeType"
          control={control}
          render={({ field }) => (
            <FormControl fullWidth>
              <InputLabel id="scope-label">Scope type</InputLabel>
              <Select labelId="scope-label" label="Scope type" {...field}>
                {SCOPE_TYPES.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        />
        <TextField label="Unit (optional)" {...register('unit')} />
        <TextField
          label="Price per unit"
          type="number"
          inputProps={{ step: '0.01' }}
          {...register('pricePerUnit')}
          error={!!errors.pricePerUnit}
        />

        {isEdit && product?.scheduledPrice && (
          <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Scheduled price pending
            </Typography>
            <Typography variant="caption" display="block" color="text.secondary">
              {product.scheduledPrice.pricePerUnit} effective{' '}
              {new Date(product.scheduledPrice.effectiveFrom).toLocaleString()}
            </Typography>
            <Controller
              name="cancelScheduledPrice"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  sx={{ mt: 1 }}
                  control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
                  label="Cancel scheduled price on save"
                />
              )}
            />
          </Box>
        )}

        <Typography variant="subtitle2" sx={{ pt: 1 }}>
          Schedule a future price (optional)
        </Typography>
        <Controller
          name="scheduleNewPrice"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
              label="Set or replace scheduled future price"
            />
          )}
        />
        {scheduleNewPrice && (
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label="Future price per unit"
              type="number"
              inputProps={{ step: '0.01' }}
              {...register('scheduledPricePerUnit')}
              error={!!errors.scheduledPricePerUnit}
              helperText={errors.scheduledPricePerUnit?.message}
              fullWidth
            />
            <TextField
              label="Effective from (local)"
              type="datetime-local"
              fullWidth
              InputLabelProps={{ shrink: true }}
              {...register('scheduledEffectiveFrom')}
              error={!!errors.scheduledEffectiveFrom}
              helperText={errors.scheduledEffectiveFrom?.message}
            />
          </Stack>
        )}

        <Controller
          name="isMultipleAllowed"
          control={control}
          render={({ field }) => (
            <FormControlLabel
              control={<Switch checked={field.value} onChange={(_, v) => field.onChange(v)} />}
              label="Multiple allowed"
            />
          )}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
          <TextField
            label="Valid from (local)"
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            {...register('validFrom')}
          />
          <TextField
            label="Valid until (local)"
            type="datetime-local"
            fullWidth
            InputLabelProps={{ shrink: true }}
            {...register('validUntil')}
          />
        </Stack>

        {kind === PRODUCT_KINDS.QUOTA && (
          <Stack direction={{ xs: 'column', sm: 'row' }} gap={2}>
            <TextField
              label="Quota key"
              {...register('quotaKey')}
              error={!!errors.quotaKey}
              helperText={errors.quotaKey?.message}
              fullWidth
            />
            <TextField
              label="Quantity per unit"
              type="number"
              {...register('quantityPerUnit')}
              error={!!errors.quantityPerUnit}
              fullWidth
            />
          </Stack>
        )}

        {kind === PRODUCT_KINDS.FEATURE && (
          <TextField
            label="Feature key"
            {...register('featureKey')}
            error={!!errors.featureKey}
            helperText={errors.featureKey?.message}
            fullWidth
          />
        )}

        {kind === PRODUCT_KINDS.PACKAGE && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Package config (JSON)
            </Typography>
            <TextField
              {...register('packageConfigJson')}
              error={!!errors.packageConfigJson}
              helperText={errors.packageConfigJson?.message}
              multiline
              minRows={10}
              fullWidth
              sx={{ fontFamily: 'monospace' }}
            />
          </Box>
        )}
      </Stack>
    </Modal>
  );
}
